/**
 * Signing in ([D-043](../../../docs/DECISIONS.md#d-043), [D-177](../../../docs/DECISIONS.md#d-177)).
 *
 * A device that has never been used shows a login: a grown-up logs in once, and
 * that signs the device in ([Setup](./Setup.tsx)). From then on the app opens on
 * the children's faces. A child never types a username, and usually never types
 * anything at all — only the one who chose a code types it.
 *
 * Every failure is the same message on purpose. The server answers an unknown
 * device, a revoked one and a wrong secret identically
 * ([Security](../../../docs/SECURITY.md)), and this screen does not invent a
 * distinction the server refused to make.
 */

import '@capytalk/tokens/blocks/picker.css';

import type { ProfileRecord } from '@capytalk/server';
import { useEffect, useState } from 'react';
import Animal from './Animal.js';
import { type Client, type Session, type Trouble, troubleFrom } from './api.js';
import { forgetDevice, readDevice } from './device.js';
import Setup from './Setup.js';

/** A face on the picker, with the title under the name when the server sends one. */
type Profile = ProfileRecord & { readonly title?: string | null };

function TroubleNote({ trouble }: { readonly trouble: Trouble }) {
  const says: Record<Trouble['kind'], string> = {
    rejected: 'That did not work. Try again.',
    locked: 'Too many tries. Wait a little, then try again.',
    missing: 'That is not here any more.',
    offline: 'No connection. Is the wi-fi on?',
    unknown: 'Something went wrong. Try again.',
  };
  return (
    <p className="sub" role="alert" style={{ marginTop: 10 }}>
      {says[trouble.kind]}
    </p>
  );
}

/** The child taps their face, and types a code only if they chose one. */
function PickProfile({
  client,
  deviceId,
  credential,
  profiles,
  onSignedIn,
  onGrownUps,
}: {
  readonly client: Client;
  readonly deviceId: string;
  readonly credential: string;
  readonly profiles: readonly Profile[];
  readonly onSignedIn: (session: Session) => void;
  /** The grown-up screen, behind the grown-up's password. */
  readonly onGrownUps: () => void;
}) {
  const [pinFor, setPinFor] = useState<Profile | null>(null);
  const [pin, setPin] = useState('');
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [busy, setBusy] = useState(false);

  async function start(profile: Profile, code?: string) {
    setBusy(true);
    setTrouble(null);
    try {
      const result = await client.auth.start.mutate({
        deviceId,
        credential,
        learnerId: profile.learnerId,
        ...(code === undefined ? {} : { pin: code }),
      });
      onSignedIn({ token: result.token, learnerId: profile.learnerId, deviceId });
    } catch (error) {
      setTrouble(troubleFrom(error));
    } finally {
      setBusy(false);
    }
  }

  if (pinFor !== null) {
    return (
      <form
        className="pad center grow"
        onSubmit={(event) => {
          event.preventDefault();
          void start(pinFor, pin);
        }}
      >
        {/* Their own animal, the one they picked (D-158). */}
        <Animal
          species={pinFor.avatarSpecies}
          background={pinFor.avatarBackground}
          width={74}
          uid="pin"
        />
        <h2>Hi, {pinFor.displayName}!</h2>
        <p className="sub">Type your code</p>
        <input
          className="chip"
          style={{ marginTop: 12, textAlign: 'center', letterSpacing: 6, width: 160 }}
          value={pin}
          inputMode="numeric"
          type="password"
          aria-label="Code"
          autoFocus
          onChange={(e) => {
            setPin(e.target.value);
          }}
        />
        {trouble !== null && <TroubleNote trouble={trouble} />}
        <button
          className="btn"
          type="submit"
          disabled={busy}
          style={{ marginTop: 14, width: 200 }}
        >
          Start
        </button>
        <button
          className="btn ghost"
          type="button"
          style={{ marginTop: 10, width: 200 }}
          onClick={() => {
            setPinFor(null);
            setPin('');
            setTrouble(null);
          }}
        >
          Back
        </button>
      </form>
    );
  }

  return (
    <div className="pad grow">
      <div className="bubble" style={{ marginTop: 6 }}>
        Who is playing today?
      </div>

      {profiles.length === 0 ? (
        <p className="sub" style={{ marginTop: 16 }}>
          There are no children on this device yet.
        </p>
      ) : (
        <div
          className="grid2"
          style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 16 }}
        >
          {profiles.map((profile, at) => (
            <button
              key={profile.learnerId}
              type="button"
              className="who"
              disabled={busy}
              onClick={() => {
                // Only the child who chose a code is asked for one; everyone
                // else is signed in by the tap itself.
                if (profile.hasPin) setPinFor(profile);
                else void start(profile);
              }}
            >
              <Animal
                species={profile.avatarSpecies}
                background={profile.avatarBackground}
                width={60}
                uid={`who-${String(at)}`}
              />
              <span className="n">{profile.displayName}</span>
              {/* Their title, under their name (D-159). An older server sends none. */}
              {typeof profile.title === 'string' && (
                <span className="title-badge">{profile.title}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {trouble !== null && <TroubleNote trouble={trouble} />}

      {/* Small and at the bottom: a child never needs it, and a grown-up looks
          for it. It asks for the grown-up's password before anything else. */}
      <button
        type="button"
        className="btn ghost"
        style={{ marginTop: 24 }}
        onClick={onGrownUps}
      >
        Grown-ups
      </button>
    </div>
  );
}

export default function SignIn({
  client,
  onSignedIn,
  onGrownUps,
  buildOwner,
}: {
  readonly client: Client;
  readonly onSignedIn: (session: Session) => void;
  /** The grown-up screen, from the faces. */
  readonly onGrownUps: () => void;
  /** How the login builds its own client. Injected by tests; nothing else passes it. */
  readonly buildOwner?: (token: () => string | null) => Client;
}) {
  /** The device this browser remembers, if a grown-up has logged in on it. */
  const [device, setDevice] = useState(readDevice);
  const [profiles, setProfiles] = useState<readonly Profile[] | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  /** Bumped by "Try again", to ask once more after a dropped connection. */
  const [attempt, setAttempt] = useState(0);

  /*
    The device proves itself with what it remembers (D-131). A credential the
    server now refuses — the device was blocked — is forgotten, and the login
    comes back, which is what blocking is for. No connection keeps it.
  */
  useEffect(() => {
    if (device === null) return;
    let live = true;
    setTrouble(null);
    void (async () => {
      try {
        const result = await client.auth.profiles.mutate(device);
        if (live) setProfiles(result.profiles);
      } catch (error) {
        if (!live) return;
        const found = troubleFrom(error);
        if (found.kind === 'offline') {
          setTrouble(found);
        } else {
          forgetDevice();
          setDevice(null);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [client, device, attempt]);

  if (device === null) {
    return (
      <Setup
        onReady={() => {
          setProfiles(null);
          setDevice(readDevice());
        }}
        {...(buildOwner === undefined ? {} : { build: buildOwner })}
      />
    );
  }

  return (
    <div className="frame">
      <div className="pad" style={{ paddingTop: 18 }}>
        <p className="kicker">CapyTalk</p>
      </div>

      {profiles !== null ? (
        <PickProfile
          client={client}
          deviceId={device.deviceId}
          credential={device.credential}
          profiles={profiles}
          onSignedIn={onSignedIn}
          onGrownUps={onGrownUps}
        />
      ) : trouble !== null ? (
        <div className="pad">
          <TroubleNote trouble={trouble} />
          <button
            className="btn"
            type="button"
            style={{ marginTop: 14 }}
            onClick={() => {
              setAttempt((n) => n + 1);
            }}
          >
            Try again
          </button>
        </div>
      ) : (
        <p className="sub pad" role="status">
          One moment…
        </p>
      )}
    </div>
  );
}
